import { bedsideDescription, isBedsideSelections, type BedsideBed, type BedsideSelection } from '../config/blissBedsideAddons'
import type { BlissBasicModelId } from '../config/blissBasic'
import type { BlissBasicConfiguratorSelections } from '../config/blissBasicConfigurator'
import type { BlissRoomPlanRecord } from '../config/blissRoomPlan'
import { customDiscoverySummary } from '../config/blissCustomDiscovery'

const BASIC_MODEL_IDS = new Set<BlissBasicModelId>([
  'twin_full',
  'full_full',
  'triple_stack',
  'quad_corner',
  'not_sure',
])

function splitName(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return { firstName: '', lastName: '' }
  if (parts.length === 1) return { firstName: parts[0], lastName: '' }
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') }
}

export function blissRoomPlanSummaryNotes(plan: BlissRoomPlanRecord): string {
  const lines: string[] = ['Design room plan (design.blissbunkbeds.com)']
  const m = plan.room?.measurements
  if (m?.lengthFt && m?.widthFt) {
    lines.push(`Room: ${m.lengthFt}' × ${m.widthFt}'${m.ceilingHeightFt ? ` × ${m.ceilingHeightFt}' ceiling` : ''}`)
  }
  const doors = plan.room?.doors?.length ?? 0
  const windows = plan.room?.windows?.length ?? 0
  if (doors || windows) lines.push(`Openings: ${doors} door(s), ${windows} window(s)`)
  if (plan.layout?.bunkModelLabel || plan.layout?.bunkModelId) {
    lines.push(`Layout interest: ${plan.layout.bunkModelLabel || plan.layout.bunkModelId}`)
  }
  if (plan.layout?.stairPlacement) lines.push(`Stairs: ${plan.layout.stairPlacement}`)
  if (plan.layout?.bunkPlacement) lines.push(`Placement: ${plan.layout.bunkPlacement}`)
  const prem = plan.premiumPreferences
  const premFlags = [
    prem?.customStaircase ? 'custom staircase' : null,
    prem?.towerPosts ? 'tower posts' : null,
    prem?.ledLighting ? 'LED lighting' : null,
    prem?.drawersCubbies ? 'drawers/cubbies' : null,
  ].filter(Boolean)
  if (premFlags.length) lines.push(`Premium interests: ${premFlags.join(', ')}`)
  if (prem?.notes?.trim()) lines.push(prem.notes.trim())
  lines.push(...customDiscoverySummary(prem?.customDiscovery))
  if (plan.layout?.layoutNotes?.trim()) lines.push(plan.layout.layoutNotes.trim())
  const rooms = plan.layout?.rooms ?? []
  if (rooms.length > 1) {
    lines.push(`Quote covers ${rooms.length} bunk rooms`)
    rooms.forEach((r, i) => lines.push(roomNote(`Room ${i + 1} (${r.name})`, r.configuratorSnapshot)))
  } else {
    const pieces = piecesNote(plan.layout?.configuratorSnapshot)
    if (pieces) lines.push(pieces)
  }
  return lines.join(' · ')
}

type NoteSnapshot = {
  room?: { width?: number; depth?: number; height?: number }
  beds?: (BedsideBed & {stairStyle?: 'open' | 'enclosed' | 'stringer'})[]
  bedsideAddons?: BedsideSelection[]
  capacity?: { current?: number | null; additional?: number | null; occupants?: string[]; storageRequested?: boolean }
}

function piecesNote(snap: unknown) {
  const beds = (snap as NoteSnapshot | undefined)?.beds
  if (!Array.isArray(beds) || !beds.length) return ''
  return `Pieces: ${beds.map((b) => `${b.name || 'Item'}${b.kind === 'stairs' ? ' (stair)' : ''}${b.stairStyle==='stringer'?' · exposed stringer stairs with two 4×4 posts, open risers, no handrails or stair drawers':b.stairStyle==='open'?' · open staircase sides without handrails':''}`).join(', ')}`
}

function roomNote(label: string, snap: unknown) {
  const s = (snap ?? {}) as NoteSnapshot
  const ft = (inches?: number) => (typeof inches === 'number' ? `${Math.round((inches / 12) * 10) / 10}'` : '?')
  const parts = [label]
  if (s.room) parts.push(`${ft(s.room.width)} × ${ft(s.room.depth)}${s.room.height ? ` × ${ft(s.room.height)} ceiling` : ''}`)
  const pieces = piecesNote(snap)
  if (pieces) parts.push(pieces)
  const c = s.capacity
  if (c && (c.current != null || c.additional != null))
    parts.push(`Sleeping goal: ${(c.current ?? 0) + (c.additional ?? 0)}`)
  if (c?.occupants?.length) parts.push(`For: ${c.occupants.map((o) => o.replace(/_/g, ' ')).join(', ')}`)
  if (c?.storageRequested) parts.push('Drawer storage requested')
  if(isBedsideSelections(s.bedsideAddons)) parts.push(...s.bedsideAddons.map(item=>bedsideDescription(item,s.beds??[])))
  return parts.join(' — ')
}

/** Map saved plan into Bliss Basic configurator fields (production models only). */
export function applyBlissRoomPlanToBasicSelections(
  plan: BlissRoomPlanRecord,
  prev: BlissBasicConfiguratorSelections,
): BlissBasicConfiguratorSelections {
  const contact = plan.contact ?? {}
  const { firstName, lastName } = contact.name
    ? splitName(contact.name)
    : { firstName: prev.firstName, lastName: prev.lastName }

  let modelId = prev.modelId
  const rawModel = plan.layout?.bunkModelId?.trim()
  if (rawModel && BASIC_MODEL_IDS.has(rawModel as BlissBasicModelId)) {
    modelId = rawModel as BlissBasicModelId
  }

  const notes = blissRoomPlanSummaryNotes(plan)

  return {
    ...prev,
    firstName: firstName || prev.firstName,
    lastName: lastName || prev.lastName,
    email: contact.email?.trim() || prev.email,
    phone: contact.phone?.trim() || prev.phone,
    projectZip: contact.zip?.trim() || prev.projectZip,
    modelId,
    notes: prev.notes?.trim() ? `${prev.notes}\n\n${notes}` : notes,
    linkedQuoteRequestId: prev.linkedQuoteRequestId,
  }
}

export function applyBlissRoomPlanToLeadGate(plan: BlissRoomPlanRecord): {
  fullName: string
  email: string
  phone: string
  projectZip: string
  funnelResumeContact: {
    firstName: string
    lastName: string
    email: string
    phone: string
    projectZip: string
  }
  projectContext: Record<string, unknown>
} {
  const contact = plan.contact ?? {}
  const { firstName, lastName } = contact.name ? splitName(contact.name) : { firstName: '', lastName: '' }
  const fullName = contact.name?.trim() || `${firstName} ${lastName}`.trim()
  const email = contact.email?.trim() || ''
  const phone = contact.phone?.trim() || ''
  const projectZip = contact.zip?.trim() || ''

  return {
    fullName,
    email,
    phone,
    projectZip,
    funnelResumeContact: { firstName, lastName, email, phone, projectZip },
    projectContext: {
      blissRoomPlan: {
        planId: plan.id,
        contact: plan.contact,
        room: plan.room,
        layout: plan.layout,
        premiumPreferences: plan.premiumPreferences,
        summary: blissRoomPlanSummaryNotes(plan),
        handoffSource: plan.attribution?.source || 'design',
      },
    },
  }
}

export function blissRoomPlanHasPremiumTailoring(plan: BlissRoomPlanRecord): boolean {
  const p = plan.premiumPreferences
  return Boolean(
    p?.customStaircase ||
      p?.towerPosts ||
      p?.ledLighting ||
      p?.drawersCubbies ||
      customDiscoverySummary(p?.customDiscovery).length > 0 ||
      p?.notes?.trim(),
  )
}
